/**
 * Affiliate-specific error shapes.
 *
 * The affiliate auth endpoints report failure inside a 200 response instead of
 * an HTTP error status and carry no `success` flag, so `apiClient` sees a
 * success. The service layer converts those bodies into rejections that look
 * like Axios errors and therefore flow through `getApiErrorMessage`, keeping the
 * server's own copy ("Invalid credentials", "Email already registered").
 */
import { HTTP_STATUS } from '../../constants';
import type { ApiError } from '../../types';
import type { AffiliateReviewState } from './affiliateTypes';

export const createAffiliateRequestError = (
  message: string | null | undefined,
  status: number = HTTP_STATUS.BAD_REQUEST,
): ApiError => ({
  response: {
    status,
    data: message ? { message } : {},
  },
});

/**
 * The affiliate application is reviewed by an operator, so a correct password
 * still cannot sign in until the account is approved. Screens need to tell that
 * apart from a credential failure to show the right copy.
 *
 * `state` carries which non-approved state the account is in — awaiting review,
 * refused, or suspended after having been approved — because each one needs a
 * different explanation and a different next step.
 */
export interface AffiliateNotApprovedError extends ApiError {
  isAffiliateNotApproved: true;
  state: AffiliateReviewState;
}

export const createAffiliateNotApprovedError = (
  state: AffiliateReviewState,
): AffiliateNotApprovedError => ({
  isAffiliateNotApproved: true,
  state,
  response: {
    status: HTTP_STATUS.FORBIDDEN,
    data: {},
  },
});

export const isAffiliateNotApprovedError = (
  error: unknown,
): error is AffiliateNotApprovedError =>
  typeof error === 'object' &&
  error !== null &&
  (error as { isAffiliateNotApproved?: boolean }).isAffiliateNotApproved === true;

/**
 * Why `SendInfluencerPayout` refused on balance grounds.
 *
 * The API guards over-payment against the affiliate's *unpaid eligible*
 * balance, and answers one of two sentences:
 *
 *   "Payout amount exceeds the affiliate's unpaid eligible balance of {amount}."
 *   "This affiliate has no unpaid eligible balance to pay out."
 *
 * Both are about the amount field, not about the request, so the payout dialog
 * attaches them to that field instead of showing them as a submit failure that
 * reads like an outage. The server's own wording is what gets rendered —
 * classifying it is all that happens here — so the operator sees the balance
 * figure the API actually computed rather than one this client inferred.
 *
 * Matched on the distinguishing phrase rather than the whole sentence so a
 * reworded message still lands on the right field; an unrecognised message
 * falls back to the submit banner and is never swallowed.
 */
export type AffiliatePayoutBalanceIssue = 'exceedsBalance' | 'noBalance';

export const affiliatePayoutBalanceIssue = (
  message: string | null | undefined,
): AffiliatePayoutBalanceIssue | null => {
  if (!message) {
    return null;
  }
  const normalized = message.toLowerCase();
  if (!normalized.includes('eligible balance')) {
    return null;
  }
  if (normalized.includes('no unpaid eligible balance')) {
    return 'noBalance';
  }
  return normalized.includes('exceeds') ? 'exceedsBalance' : null;
};
